import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import MfaSetup from "./pages/MfaSetup";
import MfaChallenge from "./pages/MfaChallenge";
import SettingsMfa from "./pages/SettingsMfa";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PlaidPrivacy from "./pages/PlaidPrivacy";
import Terms from "./pages/Terms";
import VendorDashboard from "./pages/VendorDashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import Properties from "./pages/Properties";
import Payments from "./pages/Payments";
import BankAccount from "./pages/BankAccount";
import Payouts from "./pages/Payouts";
import AdminPortal from "./pages/AdminPortal";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "account_manager" ? "/admin/portal" : user.role === "vendor" ? "/vendor" : "/employee"} replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === "vendor" ? "/vendor" : "/employee"} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/plaid-privacy" element={<PlaidPrivacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/mfa-setup" element={<MfaSetup />} />
      <Route path="/login/mfa" element={<MfaChallenge />} />
      <Route
        path="/settings/mfa"
        element={
          <ProtectedRoute>
            <SettingsMfa />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor"
        element={
          <ProtectedRoute allowedRoles={["vendor"]}>
            <VendorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee"
        element={
          <ProtectedRoute allowedRoles={["employee"]}>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute allowedRoles={["vendor"]}>
            <Properties />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <Payments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank-account"
        element={
          <ProtectedRoute>
            <BankAccount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payouts"
        element={
          <ProtectedRoute>
            <Payouts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/portal"
        element={
          <ProtectedRoute allowedRoles={["account_manager"]}>
            <AdminPortal />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
